interface VideoRecord {
  id: string;
  blob: Blob;
  url: string;
  timestamp: number;
}

// Init
export const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('InterviewVideos', 2);

    request.onupgradeneeded = event => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('videos')) {
        db.createObjectStore('videos', { keyPath: 'id' });
      }
    };

    request.onsuccess = event => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = event => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
};

// Save video to IndexedDB
export const saveVideo = async (questionId: number, blob: Blob, url: string): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction(['videos'], 'readwrite');
    const store = transaction.objectStore('videos');

    const videoId = `question_${questionId}`;
    console.log(`Saving video with ID: ${videoId}`);

    const videoRecord: VideoRecord = {
      id: videoId,
      blob,
      url,
      timestamp: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const request = store.put(videoRecord);

      request.onsuccess = () => {
        console.log(`Video for question ${questionId} saved successfully`);
        resolve();
      };

      request.onerror = event => {
        console.error('Error saving video:', (event.target as IDBRequest).error);
        reject((event.target as IDBRequest).error);
      };
    });
  } catch (error) {
    console.error('Failed to save video:', error);
    throw error;
  }
};

// Get video from IndexedDB
export const getVideo = async (questionId: number): Promise<VideoRecord | null> => {
  try {
    const db = await initDB();
    const transaction = db.transaction(['videos'], 'readonly');
    const store = transaction.objectStore('videos');

    return new Promise((resolve, reject) => {
      const request = store.get(`question_${questionId}`);

      request.onsuccess = () => {
        if (request.result) {
          // Create a fresh URL from the stored blob
          const freshUrl = URL.createObjectURL(request.result.blob);

          // Return with updated URL
          resolve({
            ...request.result,
            url: freshUrl,
          });
        } else {
          resolve(null);
        }
      };

      request.onerror = event => {
        reject((event.target as IDBRequest).error);
      };
    });
  } catch (error) {
    console.error('Failed to get video:', error);
    return null;
  }
};

// Delete video from IndexedDB
export const deleteVideo = async (questionId: number): Promise<void> => {
  try {
    const db = await initDB();
    const transaction = db.transaction(['videos'], 'readwrite');
    const store = transaction.objectStore('videos');

    return new Promise((resolve, reject) => {
      const request = store.delete(`question_${questionId}`);

      request.onsuccess = () => {
        console.log(`Video for question ${questionId} deleted from IndexedDB`);
        resolve();
      };

      request.onerror = event => {
        reject((event.target as IDBRequest).error);
      };
    });
  } catch (error) {
    console.error('Failed to delete video:', error);
    throw error;
  }
};

// Get all stored videos
export const getAllVideos = async (): Promise<VideoRecord[]> => {
  try {
    const db = await initDB();
    const transaction = db.transaction(['videos'], 'readonly');
    const store = transaction.objectStore('videos');

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = () => {
        // Create fresh URLs for all videos
        const videos = request.result.map(video => ({
          ...video,
          url: URL.createObjectURL(video.blob),
        }));
        resolve(videos || []);
      };

      request.onerror = event => {
        reject((event.target as IDBRequest).error);
      };
    });
  } catch (error) {
    console.error('Failed to get all videos:', error);
    return [];
  }
};
